import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Brain,
  Heart,
  Users,
  Calendar,
  MessageCircle,
  Shield,
  Clock,
  Star,
  ArrowRight,
  CheckCircle,
  Video,
  Building2,
  Stethoscope,
  Activity,
  UserCheck,
  HeadphonesIcon,
  FileText,
  Globe
} from 'lucide-react';

export const ServicesShowcase = () => {
    const platformFeatures = [
    {
      icon: Video,
      title: "HD Video Consultations",
      description: "Crystal clear, secure video calls with your mental health provider",
      color: "blue"
    },
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "AI-powered appointment scheduling that works with your schedule",
      color: "green"
    },
    {
      icon: MessageCircle,
      title: "Secure Messaging",
      description: "HIPAA-compliant messaging between appointments",
      color: "purple"
    },
    {
      icon: FileText,
      title: "Digital Records",
      description: "Access your treatment history and documents anytime",
      color: "amber"
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Bank-level security protects your sensitive information",
      color: "red"
    },
    {
      icon: Globe,
      title: "Nationwide Access",
      description: "Connect with providers across all 50 states",
      color: "indigo"
    }
  ];
  const mentalHealthServices = [
    {
      icon: Brain,
      title: "Psychiatric Evaluations",
      description: "Comprehensive mental health assessments with personalized treatment plans",
      features: ["Initial consultations", "Diagnostic assessments", "Treatment planning", "Follow-up care"],
      color: "blue",
  gradient: { start: "#3b82f6", end: "#2563eb" } 
    },
    {
      icon: Heart,
      title: "Therapy Sessions",
      description: "Individual, family, and group therapy with licensed professionals",
      features: ["CBT & DBT therapy", "Family counseling", "Group sessions", "Crisis support"],
      color: "purple",
gradient: { start: "#8b5cf6", end: "#7c3aed" }    },
    {
      icon: Stethoscope,
      title: "Medication Management",
      description: "Expert psychiatric medication prescribing and monitoring",
      features: ["Medication reviews", "Side effect monitoring", "Dosage adjustments", "Drug interactions"],
      color: "green",
     gradient: { start: "#10b981", end: "#059669" }
    },
    {
      icon: Users,
      title: "Specialized Care",
      description: "Targeted treatment for specific mental health conditions",
      features: ["ADHD treatment", "Depression & anxiety", "Bipolar disorder", "PTSD therapy"],
      color: "amber",
      gradient: { start: "#f59e0b", end: "#d97706" }
    }
  ];



  return (
    <div className="min-h-screen bg-gray-50">
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-blue-100 text-blue-800 text-lg px-6 py-3 rounded-full mb-8">
            <Brain className="w-5 h-5 mr-2" />
            Mental Health Services
          </Badge>

          <h1 className="text-6xl text-gray-900 mb-6 leading-tight">
            Comprehensive Mental Healthcare
            <span className="text-blue-600 block">Designed for You</span>
          </h1>

          <p className="text-2xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed">
            Expert psychiatric care delivered through innovative technology, personalized treatment plans,
            and compassionate professionals who understand your unique needs.
          </p>

          <div className="button-group-container">
            <Button className="btn-primary-lg" asChild>
              <Link to="/signup" className="link-no-underline_one">
                <Calendar className="icon-spacing icon-margin-right" />
                Start Your Journey
                <ArrowRight className="icon-spacing icon-margin-left" />
              </Link>
            </Button>

            <Button className="btn-outline-lg" asChild>
              <Link to="/providers" className="link-no-underline">
                <Users className="icon-spacing icon-margin-right" />
                Meet Our Providers
              </Link>
            </Button>
          </div>

        </div>
      </section>
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-gray-900 mb-4">Our Mental Health Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive care tailored to your specific mental health needs and goals
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {mentalHealthServices.map((service, index) => (
              <Card key={index} className="custom-card_mentalHealthServices">
                <CardContent className="p-0">
                <div
  className="service-card"
  style={{
    '--gradient-start': service.gradient.start,
    '--gradient-end': service.gradient.end,
  }}
>

                    <div className="service-card-header">
                      <div className="service-icon-container">
                        <service.icon className="service-icon" />
                      </div>
                      <div className="service-text">
                        <h3>{service.title}</h3>
                        <p>{service.description}</p>
                      </div>
                    </div>
                  </div>


                  <div className="p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-3">
                          <div className={`w-8 h-8 bg-${service.color}-100 rounded-lg flex items-center justify-center`}>
                            <CheckCircle className={`h-4 w-4 text-${service.color}-600`} />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className={`w-full bg-${service.color}-600 hover:bg-${service.color}-700 text-white py-4 text-lg rounded-xl`}
                      asChild
                    >
                      <Link to="/booking/telehealth"
                      className='no-underline'
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        Book {service.title}
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-gray-900 mb-4">Advanced Platform Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge technology designed to make your mental health journey seamless and secure
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {platformFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 text-center hover:bg-white hover:shadow-lg transition-all duration-300 border border-gray-100">
                <div className={`w-20 h-20 bg-${feature.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  <feature.icon className={`h-10 w-10 text-${feature.color}-600`} />
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    <section className="custom-section-gradient">
  <div className="custom-container-centered">
          <h2 className="text-4xl text-white mb-12">Trusted Mental Healthcare</h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: UserCheck, number: "50+", label: "Licensed Psychiatrists" },
              { icon: Star, number: "4.9/5", label: "Patient Satisfaction" },
              { icon: Shield, number: "100%", label: "HIPAA Compliant" },
              { icon: HeadphonesIcon, number: "24/7", label: "Crisis Support" }
            ].map((stat, index) => (
              <div key={index} className="space-y-4">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto">
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
                <div className="text-4xl text-white font-bold">{stat.number}</div>
                <div className="text-xl text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-blue-700 hover:bg-gray-100 px-10 py-6 text-xl h-auto rounded-xl"
              asChild
            >
              <Link to="/signup" className='no-underline'>
                <Heart className="mr-3 h-6 w-6" />
                Begin Your Mental Health Journey
                <ArrowRight className="ml-3 h-6 w-6" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};